import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  HeartIcon,
  ClockIcon,
  TrashIcon,
  BellIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';

// Mock data
const mockWatchlist = [
  {
    id: 1,
    name: 'Equus Dualis II #03',
    collection: 'Equus Dualis II',
    image: '/Equus Dualis/Equus Dualis II ) 3.png',
    currentBid: 68.5,
    myAlert: 60,
    currency: 'ETH',
    endTime: new Date(Date.now() + 3600000 * 5),
    auctionType: 'English',
    totalBids: 23,
    hasNotification: true,
  },
  {
    id: 2,
    name: 'Warrior Mythology #09',
    collection: 'Warrior Mythology',
    image: '/warrior mythology/warrior mythology9.jpg',
    currentBid: 4.8,
    myAlert: null,
    currency: 'ETH',
    endTime: new Date(Date.now() + 3600000 * 12),
    auctionType: 'Dutch',
    totalBids: 0,
    hasNotification: false,
  },
  {
    id: 3,
    name: 'Equus Dualis II #21',
    collection: 'Equus Dualis II',
    image: '/Equus Dualis/Equus Dualis II ) 21.png',
    currentBid: 12.5,
    myAlert: 10,
    currency: 'ETH',
    endTime: new Date(Date.now() + 3600000 * 24),
    auctionType: 'English',
    totalBids: 31,
    hasNotification: true,
  },
  {
    id: 4,
    name: 'Warrior Mythology #35',
    collection: 'Warrior Mythology',
    image: '/warrior mythology/warrior mythology35.jpg',
    currentBid: 15.3,
    myAlert: null,
    currency: 'ETH',
    endTime: new Date(Date.now() + 3600000 * 2),
    auctionType: 'Candle',
    totalBids: 19,
    hasNotification: false,
  },
];

const WatchlistPage: React.FC = () => {
  const [watchlist, setWatchlist] = useState(mockWatchlist);
  const [filter, setFilter] = useState('all');

  const removeFromWatchlist = (id: number) => {
    setWatchlist(watchlist.filter(item => item.id !== id));
  };

  const toggleNotification = (id: number) => {
    setWatchlist(watchlist.map(item =>
      item.id === id ? { ...item, hasNotification: !item.hasNotification } : item
    ));
  };

  const formatTimeRemaining = (endTime: Date) => {
    const now = new Date();
    const diff = endTime.getTime() - now.getTime();

    if (diff <= 0) return 'Finalizada';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    return `${hours}h ${minutes}m`;
  };

  const filteredItems = filter === 'all'
    ? watchlist
    : filter === 'ending_soon'
    ? [...watchlist].sort((a, b) => a.endTime.getTime() - b.endTime.getTime())
    : watchlist.filter(item => item.hasNotification);

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <HeartIcon className="w-8 h-8 text-red-400" />
            Mis Intereses
          </h1>
          <p className="text-gray-400">Items que estás siguiendo</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm ${
              filter === 'all'
                ? 'bg-primary-500 text-white'
                : 'bg-dark-800 text-gray-400 hover:text-white'
            }`}
          >
            Todos ({watchlist.length})
          </button>
          <button
            onClick={() => setFilter('ending_soon')}
            className={`px-4 py-2 rounded-lg text-sm ${
              filter === 'ending_soon'
                ? 'bg-primary-500 text-white'
                : 'bg-dark-800 text-gray-400 hover:text-white'
            }`}
          >
            Terminan Pronto
          </button>
          <button
            onClick={() => setFilter('alerts')}
            className={`px-4 py-2 rounded-lg text-sm ${
              filter === 'alerts'
                ? 'bg-primary-500 text-white'
                : 'bg-dark-800 text-gray-400 hover:text-white'
            }`}
          >
            Con Alertas
          </button>
        </div>
      </div>

      {/* Watchlist Items */}
      {filteredItems.length > 0 ? (
        <div className="space-y-4">
          {filteredItems.map((item, idx) => {
            const isEndingSoon = item.endTime.getTime() - Date.now() < 3600000 * 3;
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-dark-800/50 rounded-xl border border-dark-700 p-4 hover:border-primary-500/30 transition-all"
              >
                <div className="flex items-center gap-4">
                  {/* Image */}
                  <Link to={`/auction/${item.id}`} className="flex-shrink-0">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-24 h-24 rounded-xl object-cover hover:scale-105 transition-transform"
                    />
                  </Link>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-gray-500">{item.collection}</span>
                      <span className="px-2 py-0.5 bg-dark-700 text-gray-400 text-xs rounded">
                        {item.auctionType}
                      </span>
                    </div>
                    <Link to={`/auction/${item.id}`}>
                      <h3 className="text-lg font-semibold text-white hover:text-primary-400 transition-colors">
                        {item.name}
                      </h3>
                    </Link>
                    <div className="flex items-center gap-4 mt-2">
                      <div>
                        <p className="text-xs text-gray-500">Puja Actual</p>
                        <p className="text-white font-bold">{item.currentBid} {item.currency}</p>
                      </div>
                      {item.myAlert && (
                        <div>
                          <p className="text-xs text-gray-500">Mi Alerta</p>
                          <p className="text-primary-400">{item.myAlert} {item.currency}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-gray-500">Pujas</p>
                        <p className="text-gray-300">{item.totalBids}</p>
                      </div>
                    </div>
                  </div>

                  {/* Time & Actions */}
                  <div className="flex flex-col items-end gap-3">
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                      isEndingSoon ? 'bg-red-500/20 text-red-400' : 'bg-dark-700 text-white'
                    }`}>
                      <ClockIcon className="w-4 h-4" />
                      <span className="font-medium">{formatTimeRemaining(item.endTime)}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Notification toggle */}
                      <button
                        onClick={() => toggleNotification(item.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          item.hasNotification
                            ? 'bg-primary-500/20 text-primary-400'
                            : 'bg-dark-700 text-gray-400 hover:text-white'
                        }`}
                        title={item.hasNotification ? 'Desactivar notificaciones' : 'Activar notificaciones'}
                      >
                        <BellIcon className="w-5 h-5" />
                      </button>

                      {/* Remove button */}
                      <button
                        onClick={() => removeFromWatchlist(item.id)}
                        className="p-2 bg-dark-700 text-gray-400 hover:text-red-400 rounded-lg transition-colors"
                        title="Quitar de favoritos"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>

                      {/* View auction */}
                      <Link
                        to={`/auction/${item.id}`}
                        className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors text-sm"
                      >
                        Ver Subasta
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16">
          <HeartSolidIcon className="w-16 h-16 text-gray-700 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            Tu lista está vacía
          </h3>
          <p className="text-gray-400 mb-6">
            Añade items a tu watchlist para seguir su progreso
          </p>
          <Link
            to="/explore"
            className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
          >
            Explorar Subastas
          </Link>
        </div>
      )}

      {/* Info Card */}
      <div className="mt-8 bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <BellIcon className="w-6 h-6 text-blue-400 flex-shrink-0" />
          <div>
            <h3 className="text-white font-semibold mb-2">Sobre las Notificaciones</h3>
            <p className="text-gray-400 text-sm">
              Activa las notificaciones para recibir alertas cuando:
            </p>
            <ul className="mt-2 space-y-1 text-gray-400 text-sm">
              <li>• La subasta esté por terminar (últimos 30 minutos)</li>
              <li>• El precio alcance tu alerta configurada</li>
              <li>• Haya nuevas pujas significativas</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WatchlistPage;
